import type { Block, DesignSystemDto } from '@ads/shared';
import { Fragment, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { rich } from '../lib/format';

/* ─────────────────────────────────────────────────────────────────────
   O renderizador de peças. Recebe blocos tipados e os veste com o
   design system do cliente: cores, fontes, raio e sombra vêm dos papéis
   (--ds-role-*) da versão que gerou a peça. A interface do ADS não
   empresta nada — a peça é do cliente.
   ───────────────────────────────────────────────────────────────────── */

/** Injeta o CSS de uma versão do design system uma única vez. */
export function useDsStyle(ds: DesignSystemDto | null | undefined) {
  useEffect(() => {
    if (!ds) return;
    const id = `ds-${ds.hash}`;
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = ds.css;
    document.head.appendChild(el);
  }, [ds]);
}

type Group =
  | { kind: 'section'; block: Extract<Block, { type: 'section' }>; index: number; children: Item[] }
  | { kind: 'loose'; children: Item[] };
type Item = { kind: 'block'; block: Block; index: number } | { kind: 'metrics'; blocks: Array<{ block: Extract<Block, { type: 'metric' }>; index: number }> };

function group(blocks: Block[]): Group[] {
  const out: Group[] = [];
  let cur: Group = { kind: 'loose', children: [] };
  out.push(cur);
  blocks.forEach((b, index) => {
    if (b.type === 'section') { cur = { kind: 'section', block: b, index, children: [] }; out.push(cur); return; }
    const last = cur.children[cur.children.length - 1];
    if (b.type === 'metric') {
      if (last?.kind === 'metrics') last.blocks.push({ block: b, index });
      else cur.children.push({ kind: 'metrics', blocks: [{ block: b, index }] });
      return;
    }
    cur.children.push({ kind: 'block', block: b, index });
  });
  return out.filter(g => g.kind === 'section' || g.children.length);
}

export interface DocProps {
  title: string;
  org: string;
  deliverable: string;
  date: string;
  blocks: Block[];
  ds: DesignSystemDto | null | undefined;
  /** índices a partir dos quais os blocos entram animados (geração ao vivo) */
  liveFrom?: number;
  writing?: boolean;
  /** capa já pronta, blocos ainda por vir */
  pending?: boolean;
}

export function Doc({ title, org, deliverable, date, blocks, ds, liveFrom = Infinity, writing, pending }: DocProps) {
  useDsStyle(ds);
  const groups = useMemo(() => group(blocks), [blocks]);
  let sectionNo = 0;
  const live = (i: number) => i >= liveFrom;

  return (
    <article className="doc" data-ds={ds?.hash} data-writing={writing || undefined}>
      <header className="doc-cover">
        <div className="doc-cover-top">
          <span className="doc-brand">
            {ds?.logo.mono ? <img src={ds.logo.mono} alt="" /> : ds?.logo.primary ? <img src={ds.logo.primary} alt="" /> : null}
            <b>{org}</b>
          </span>
          <span className="doc-kind">{deliverable}</span>
        </div>
        <h1 className="doc-title" key={title}>{title}</h1>
        <div className="doc-cover-meta">
          <span>{date}</span>
          {ds && <span>Design system {ds.name} {ds.version}</span>}
        </div>
      </header>

      <div className="doc-body">
        {groups.map((g, gi) => {
          if (g.kind === 'loose') return <Fragment key={'l' + gi}>{g.children.map(it => <ItemView key={key(it)} it={it} live={live} />)}</Fragment>;
          sectionNo++;
          return (
            <section className="doc-sec" key={'s' + g.index} id={`sec-${g.block.id}`}>
              <header className={`doc-sec-h${live(g.index) ? ' live' : ''}`}>
                <span className="doc-sec-no">{String(sectionNo).padStart(2, '0')}</span>
                <h2>{g.block.title}</h2>
              </header>
              {g.children.map(it => <ItemView key={key(it)} it={it} live={live} />)}
            </section>
          );
        })}
        {(writing || pending) && (
          <div className="doc-writing" aria-hidden="true">
            <span className="doc-caret" />
            <i style={{ '--w': '92%' } as CSSProperties} /><i style={{ '--w': '84%' } as CSSProperties} /><i style={{ '--w': '61%' } as CSSProperties} />
          </div>
        )}
      </div>
      <footer className="doc-foot"><span>{org}</span><span>Gerado pelo ADS · Aion</span></footer>
    </article>
  );
}

const key = (it: Item) => (it.kind === 'metrics' ? 'm' + it.blocks[0]!.index : 'b' + it.index);

function ItemView({ it, live }: { it: Item; live: (i: number) => boolean }) {
  if (it.kind === 'metrics') {
    return (
      <div className={`doc-metrics n${Math.min(it.blocks.length, 4)}`}>
        {it.blocks.map(({ block: m, index }) => (
          <div className={`doc-metric${live(index) ? ' live' : ''}`} key={index}>
            <span className={`doc-metric-v${typeof m.value === 'string' && m.value.length > 7 ? ' txt' : ''}`}>{typeof m.value === 'number' ? m.value.toLocaleString('pt-BR') : m.value}{m.unit && <small>{m.unit}</small>}</span>
            <span className="doc-metric-l">{m.label}</span>
            {m.delta && <span className="doc-metric-d">{m.delta}</span>}
          </div>
        ))}
      </div>
    );
  }
  return <BlockView b={it.block} live={live(it.index)} />;
}

function BlockView({ b, live }: { b: Block; live: boolean }): ReactNode {
  const cls = (c: string) => `${c}${live ? ' live' : ''}`;
  const reveal = (len: number) => ({ '--dur': `${Math.min(1400, 380 + len * 2.2)}ms` } as CSSProperties);
  switch (b.type) {
    case 'heading': return <h3 className={cls('doc-h3')}>{b.text}</h3>;
    case 'paragraph': return <p className={cls('doc-p')} style={reveal(b.text.length)}>{rich(b.text)}</p>;
    case 'list': {
      const L = b.style === 'number' ? 'ol' : 'ul';
      return <L className={cls('doc-list')}>{b.items.map((x, i) => <li key={i} style={{ '--i': i } as CSSProperties}>{rich(x)}</li>)}</L>;
    }
    case 'quote': return <blockquote className={cls('doc-quote')}><p>{b.text}</p>{b.cite && <cite>{b.cite}</cite>}</blockquote>;
    case 'callout': return <aside className={cls('doc-callout')} data-tone={b.tone}><p>{rich(b.text)}</p></aside>;
    case 'table': return (
      <figure className={cls('doc-table')}>
        <div className="doc-table-scroll">
          <table>
            <thead><tr>{b.columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
            <tbody>{b.rows.map((r, i) => <tr key={i} style={{ '--i': i } as CSSProperties}>{r.map((c, j) => <td key={j}>{rich(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
        {b.caption && <figcaption>{b.caption}</figcaption>}
      </figure>
    );
    case 'chart': return <Chart b={b} live={live} />;
    case 'metric': return null;
    default: return null;
  }
}

function Chart({ b, live }: { b: Extract<Block, { type: 'chart' }>; live: boolean }) {
  const max = Math.max(...b.series.map(s => s.value), 1);
  const unit = b.unit ?? '';
  if (b.kind === 'bar') {
    return (
      <figure className={`doc-chart bar${live ? ' live' : ''}`}>
        {b.title && <figcaption>{b.title}</figcaption>}
        <div className="doc-bars">
          {b.series.map((s, i) => (
            <div className="doc-bar" key={i} style={{ '--v': `${(s.value / max) * 100}%`, '--i': i } as CSSProperties}>
              <span className="l">{s.label}</span><span className="t"><i /></span><span className="n">{s.value.toLocaleString('pt-BR')}{unit}</span>
            </div>
          ))}
        </div>
      </figure>
    );
  }
  const W = 560, H = 180, P = 28;
  const pts = b.series.map((s, i) => [P + (i * (W - 2 * P)) / Math.max(1, b.series.length - 1), H - P - (s.value / max) * (H - 2 * P)] as const);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <figure className={`doc-chart line${live ? ' live' : ''}`}>
      {b.title && <figcaption>{b.title}</figcaption>}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={b.title ?? 'gráfico'}>
        <line x1={P} x2={W - P} y1={H - P} y2={H - P} className="axis" />
        <path d={d} className="ln" pathLength={1} />
        {pts.map(([x, y], i) => <g key={i}><circle cx={x} cy={y} r={3.5} /><text x={x} y={H - 8} textAnchor="middle">{b.series[i]!.label}</text><text x={x} y={y - 10} textAnchor="middle" className="v">{b.series[i]!.value}{unit}</text></g>)}
      </svg>
    </figure>
  );
}
